import { create } from 'zustand'
import { configService } from '@/core/services/ConfigService'

export interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  webViewToken: string | null

  // Actions
  setToken: (token: string) => void
  setRefreshToken: (token: string) => void
  setWebViewToken: (token: string) => void
  clearWebViewToken: () => void
  clearTokens: () => void

  // Computed properties
  getToken: () => string | null
  hasWebViewToken: () => boolean
  isAuthenticated: () => boolean
  isTokenExpired: (token: string) => boolean
  getUserFromToken: (token: string) => { uid: string; email?: string; name?: string } | null
}

const ACCESS_TOKEN_KEY = configService.auth.tokenKey
const REFRESH_TOKEN_KEY = configService.auth.refreshTokenKey

const isLocalStorageAvailable = (): boolean => {
  return typeof localStorage !== 'undefined' && localStorage !== null
}

const getStoredToken = (key: string): string | null => {
  if (!isLocalStorageAvailable()) return null
  return localStorage.getItem(key)
}

const setStoredToken = (key: string, token: string | null): void => {
  if (!isLocalStorageAvailable()) return
  if (token) {
    localStorage.setItem(key, token)
  } else {
    localStorage.removeItem(key)
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: getStoredToken(ACCESS_TOKEN_KEY),
  refreshToken: getStoredToken(REFRESH_TOKEN_KEY),
  webViewToken: null,

  setToken: (token: string) => {
    setStoredToken(ACCESS_TOKEN_KEY, token)
    set({ accessToken: token })
  },

  setRefreshToken: (token: string) => {
    setStoredToken(REFRESH_TOKEN_KEY, token)
    set({ refreshToken: token })
  },

  setWebViewToken: (token: string) => {
    set({ webViewToken: token })
  },

  clearWebViewToken: () => {
    set({ webViewToken: null })
  },

  clearTokens: () => {
    setStoredToken(ACCESS_TOKEN_KEY, null)
    setStoredToken(REFRESH_TOKEN_KEY, null)
    set({ accessToken: null, refreshToken: null, webViewToken: null })
  },

  getToken: () => {
    const state = get()
    if (state.webViewToken) {
      return state.webViewToken
    }

    const token = state.accessToken
    if (token && state.isTokenExpired(token)) {
      state.clearTokens()
      return null
    }
    return token
  },

  hasWebViewToken: () => {
    return get().webViewToken !== null
  },

  isAuthenticated: () => {
    const token = get().getToken()
    return token !== null && !get().isTokenExpired(token)
  },

  isTokenExpired: (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      return payload.exp < currentTime
    } catch {
      return true
    }
  },

  getUserFromToken: (token: string): { uid: string; email?: string; name?: string } | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return {
        uid: payload.sub || payload.uid,
        email: payload.email,
        name: payload.name || payload.username
      }
    } catch {
      return null
    }
  },
}))